import { Checkbox, Tooltip } from 'antd';
import { AppContext, License } from 'jsx';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { Info } from 'react-feather';
import { useTranslation } from 'react-i18next';
import {
  PermissionSetOption,
  PermissionSetRow,
} from '../util/permissionsForEdit';
import { Cell, Row } from './Table';

const CHECKBOX_COLUMN_WIDTH = 200;

type Props = {
  perSet: PermissionSetRow;
  groupPermissions: (number | undefined)[];
  checkedList: (number | undefined)[];
  setCheckedList: Dispatch<SetStateAction<(number | undefined)[]>>;
  optionsWithViewPermission: PermissionSetOption[];
  disabled: boolean;
  license: License;
  handleChange: any;
};

export default function GroupPermissionRow({
  perSet,
  groupPermissions,
  checkedList,
  setCheckedList,
  optionsWithViewPermission,
  disabled,
  license,
  handleChange,
}: Props) {
  const { allPermissionsMap } = useContext(AppContext);
  const [t] = useTranslation(['authorization']);

  return (
    <Row>
      <Cell style={{ paddingLeft: 16 }}>{perSet.label}</Cell>
      {perSet.options.map((option: any, i: number) => {
        if (!option?.codename) {
          return <Cell key={`empty${i}`} width={CHECKBOX_COLUMN_WIDTH}></Cell>;
        }
        const permissionId = allPermissionsMap.get(option.codename);

        const isChecked = checkedList.find(
          (item: number | undefined) => item === permissionId
        );

        const isViewDisabled = optionsWithViewPermission.find((opt: any) => {
          if (Array.isArray(opt.view)) {
            return opt.view.includes(option.codename);
          }
          return opt.view === option.codename;
        });

        const isLicenseDisabled =
          license && !(option.license_codename in license);

        return (
          <Cell
            key={option.codename}
            width={CHECKBOX_COLUMN_WIDTH}
            style={{ justifyContent: 'center' }}
          >
            <div style={{ position: 'relative' }}>
              <Checkbox
                data-ta={option.codename}
                checked={!!isChecked}
                disabled={
                  disabled ||
                  option.disabled ||
                  !!isViewDisabled ||
                  !!isLicenseDisabled
                }
                key={option.codename}
                onChange={e => {
                  if (e.target.checked) {
                    if (option.view) {
                      // In case there are several view permissions to check
                      const viewOption = Array.isArray(option.view)
                        ? option.view.map((codename: string) =>
                            allPermissionsMap.get(codename)
                          )
                        : [allPermissionsMap.get(option.view)];

                      setCheckedList([
                        ...checkedList,
                        ...viewOption,
                        permissionId,
                      ]);
                    } else {
                      setCheckedList([...checkedList, permissionId]);
                    }
                  } else {
                    const filteredCheckList = checkedList.filter(
                      item => item !== permissionId
                    );
                    setCheckedList(filteredCheckList);
                  }
                  if (permissionId)
                    handleChange(e, groupPermissions, permissionId, option);
                }}
              />
              {isLicenseDisabled && (
                <div style={{ position: 'absolute', left: 24, top: 0 }}>
                  <Tooltip
                    title={t('This module is not included in current license')}
                  >
                    <Info size={15} className="text-gray-400" />
                  </Tooltip>
                </div>
              )}
            </div>
          </Cell>
        );
      })}
    </Row>
  );
}
